import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { FaFileAlt, FaFileCsv, FaTimes } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { showSuccessAlert } from 'utils/AlertUtils';

import {
  BulkUploadMerchants,
  DownloadBulkUploadTemplate,
} from '../../../reactRedux/thunks/merchnatThunk/merchantBulkThunk';

const MAX_SIZE = 5 * 1024 * 1024;

const MerchantBulkUploadModal = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const { isBulkUploadLoading } = useSelector((state) => state.MerchantSlice);

  const [selectedFile, setSelectedFile] = useState(null);
  const [fileError, setFileError] = useState('');

  const validateAndSet = useCallback((file) => {
    setFileError('');
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setFileError('Only .csv files are accepted.');
      return;
    }
    if (file.size > MAX_SIZE) {
      setFileError('File size must not exceed 5 MB.');
      return;
    }
    setSelectedFile(file);
  }, []);

  // Listen for paste events while the modal is open
  useEffect(() => {
    if (!isOpen) return;
    const handlePaste = (e) => {
      const file = e.clipboardData?.files?.[0];
      if (file) validateAndSet(file);
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [isOpen, validateAndSet]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { 'text/csv': ['.csv'] },
    maxSize: MAX_SIZE,
    multiple: false,
    noClick: false,
    onDrop: (accepted, rejected) => {
      if (rejected?.length) {
        const err = rejected[0]?.errors?.[0];
        setFileError(
          err?.code === 'file-too-large'
            ? 'File size must not exceed 5 MB.'
            : 'Only .csv files are accepted.',
        );
        setSelectedFile(null);
        return;
      }
      if (accepted?.length) validateAndSet(accepted[0]);
    },
    onDropRejected: () => {
      setFileError('Only .csv files are accepted.');
      setSelectedFile(null);
    },
  });

  const handleClose = useCallback(() => {
    setSelectedFile(null);
    setFileError('');
    onClose();
  }, [onClose]);

  const handleRemoveFile = useCallback((e) => {
    e.stopPropagation();
    setSelectedFile(null);
    setFileError('');
  }, []);

  const handleUpload = useCallback(() => {
    if (!selectedFile) return;
    dispatch(
      BulkUploadMerchants(selectedFile, () => {
        handleClose();
        showSuccessAlert(
          'File uploaded. Merchants are being processed in the background.',
        );
      }),
    );
  }, [dispatch, selectedFile, handleClose]);

  const handleDownloadTemplate = useCallback(() => {
    dispatch(DownloadBulkUploadTemplate());
  }, [dispatch]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      closeOnOverlayClick={false}
      size="md"
    >
      <ModalOverlay backdropFilter="blur(2px)" />
      <ModalContent borderRadius="xl" boxShadow="2xl" p={2}>
        <ModalHeader fontSize="lg" fontWeight="700" pb={1}>
          Bulk Upload Merchants
        </ModalHeader>
        <ModalCloseButton top={4} right={4} />

        <ModalBody pb={4}>
          <Text fontSize="sm" color="gray.500" mb={5}>
            Download the CSV template, fill it in, then drag-and-drop or select
            the file below.
          </Text>

          {/* Dropzone */}
          <Box
            {...getRootProps()}
            border="2px dashed"
            borderColor={
              isDragActive ? 'blue.400' : fileError ? 'red.400' : 'gray.200'
            }
            borderRadius="xl"
            bg={isDragActive ? 'blue.50' : 'gray.50'}
            p={6}
            cursor="pointer"
            transition="all 0.15s ease"
            _hover={{ borderColor: 'blue.300', bg: 'blue.50' }}
            mb={3}
          >
            <input {...getInputProps()} />
            <VStack spacing={3}>
              {selectedFile ? (
                <>
                  <Icon as={FaFileCsv} boxSize={10} color="green.500" />
                  <Box textAlign="center">
                    <Text fontSize="sm" fontWeight="600" color="gray.700">
                      {selectedFile.name}
                    </Text>
                    <Text fontSize="xs" color="gray.400" mt={1}>
                      {(selectedFile.size / 1024).toFixed(1)} KB
                    </Text>
                  </Box>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorScheme="red"
                    leftIcon={<FaTimes />}
                    onClick={handleRemoveFile}
                  >
                    Remove
                  </Button>
                </>
              ) : (
                <>
                  <Icon
                    as={FaFileAlt}
                    boxSize={10}
                    color={isDragActive ? 'blue.400' : 'gray.300'}
                  />
                  <Box textAlign="center">
                    <Text fontSize="sm" fontWeight="600" color="gray.600">
                      {isDragActive
                        ? 'Drop your CSV file here'
                        : 'Drag & drop, paste, or click to select'}
                    </Text>
                    <Text fontSize="xs" color="gray.400" mt={1}>
                      CSV files only · max 5 MB
                    </Text>
                  </Box>
                </>
              )}
            </VStack>
          </Box>

          {fileError && (
            <Text fontSize="xs" color="red.500" mt={1}>
              {fileError}
            </Text>
          )}
        </ModalBody>

        <ModalFooter
          borderTop="1px solid"
          borderColor="gray.100"
          pt={4}
          justifyContent="space-between"
        >
          <Button
            variant="ghost"
            fontSize="sm"
            fontWeight="500"
            color="blue.500"
            px={0}
            _hover={{ bg: 'transparent', textDecoration: 'underline' }}
            onClick={handleDownloadTemplate}
          >
            View Sample CSV
          </Button>

          <Box display="flex" gap={3}>
            <Button
              variant="outline"
              fontSize="sm"
              fontWeight="500"
              padding="10px 20px"
              onClick={handleClose}
              isDisabled={isBulkUploadLoading}
            >
              Cancel
            </Button>
            <Button
              bg="primary"
              color="white"
              fontSize="sm"
              fontWeight="500"
              padding="10px 20px"
              _hover={{ bg: 'primary', opacity: 0.8 }}
              onClick={handleUpload}
              isLoading={isBulkUploadLoading}
              loadingText="Uploading..."
              isDisabled={!selectedFile || !!fileError}
            >
              Upload
            </Button>
          </Box>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

MerchantBulkUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MerchantBulkUploadModal;
